import React from "react";
import { useTranslation } from "../i18n";
import { m } from "framer-motion";
import { ArrowRight, MessageCircle, Layers, Plug, PlayCircle } from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import CountUp from "./reactbits/CountUp";
import FreeExtractWidget from "./FreeExtractWidget";
import { heroVariant, trackHeroExposure } from "../utils/experiment";
import "./Hero.css";
import { signupUrl, useSignupUrl } from "../utils/signup";

// En las páginas de sector no está la sección «Cómo funciona»: se lleva a la
// de la portada en vez de dejar el botón sin respuesta.
const scrollToHowItWorks = () => {
  const el = document.getElementById("how-it-works");
  if (el) el.scrollIntoView({ behavior: "smooth" });
  else window.location.assign(`/${window.location.pathname.split("/")[1] || "en"}/#how-it-works`);
};

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Hero de la portada.
 *
 * Reescrito el 16-9-2026 con la evidencia de la Ad Library (findings/
 * competencia-ads-y-embudos-2026-09-16.md). Tres cosas cambiaron y conviene
 * saber por qué, porque son tentadoras de revertir:
 *
 * 1. **El widget está DENTRO del hero.** El CTA principal ya no pide registro.
 *    Toda la estrategia es que prueben antes de crear la cuenta, y el widget
 *    estaba por debajo del pliegue en `TryFreeSection`. Quien mejor hace esto
 *    (ExtractFox, 27 herramientas gratuitas posicionadas en Google) pone la
 *    zona de subida como primer elemento de la página, sin excepción.
 *
 * 2. **Se retira el texto rotatorio.** Hacía tres cosas mal: el H1 en texto
 *    plano salía como «Automatiza la extracción defacturasfacturas», la palabra
 *    se salía de su caja durante la transición y se solapaba con el subtítulo, y
 *    su trabajo —enseñar los tipos de documento— lo hacen mejor las pestañas del
 *    widget, que además son accionables.
 *
 * 3. **El titular nombra el mecanismo y WhatsApp.** El anterior («Automatiza la
 *    extracción de datos de documentos» / «Define plantillas de extracción»)
 *    describía una categoría y vendía trabajo de configuración. Peor: «define
 *    plantillas» es literalmente el argumento que ExtractFox usa EN CONTRA de
 *    Docparser en su página comparativa. Y WhatsApp —lo único que no tiene
 *    ningún competidor de los que se anuncian— no aparecía en el hero.
 *
 * Se retiró también «Contenido 100% extraído y validado»: medimos 0,65 de
 * confianza media en fotos de móvil reales. No se sostiene.
 */
export default function Hero({ customTitle, customSubtitle, withWidget = false }) {
  const { t, lang } = useTranslation();
  const signupHref = useSignupUrl(lang);
  const thousandsSep = lang === "en" ? "," : lang === "fr" ? "\u202f" : ".";

  // Test A/B del mensaje (ver utils/experiment.js). Solo en la portada con
  // widget: las páginas de sector traen su propio subtítulo. El H1 no entra en
  // el test — es el elemento LCP y lo que indexa Google.
  //
  // Las dos variantes se pintan siempre y `Hero.css` oculta una según el
  // atributo `data-hero` que pone <html> antes del primer pintado. Así el HTML
  // prerenderizado ya trae el texto correcto para cada visitante y React no lo
  // cambia al montar.
  const copy = (key) =>
    withWidget ? (
      <>
        <span className="hero-copy-a">{t(key)}</span>
        <span className="hero-copy-b">{t(`${key}_excel`)}</span>
      </>
    ) : (
      t(key)
    );
  React.useEffect(() => {
    if (withWidget) trackHeroExposure();
  }, [withWidget]);

  // El H1 de este bloque es el elemento LCP. Antes el contenedor arrancaba en
  // `opacity: 0` con 0,2 s de retardo y 0,15 s de escalonado, así que el texto
  // más grande de la página no se pintaba hasta pasado casi un segundo — y el
  // LCP se mide cuando ese elemento se ve, no cuando existe en el DOM.
  //
  // Ahora el contenedor y el titular son visibles desde el primer fotograma. La
  // entrada escalonada se conserva SOLO para lo que va debajo (badge, subtítulo,
  // botones), que no compite con la métrica.
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /** El titular no anima: pinta ya. Es el elemento que mide el LCP. */
  const lcpSafeVariants = {
    hidden: { opacity: 1, y: 0 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      aria-labelledby="hero-title"
      className={cn(
        "relative overflow-hidden",
        // Sin `min-h-[90vh]`: esa altura empujaba el widget fuera de la pantalla
        // en portátiles, que es justo lo que este cambio viene a corregir.
        withWidget ? "pt-5 pb-12 md:pt-24 md:pb-24" : "pt-10 pb-14 md:pt-40 md:pb-28"
      )}
    >
      {/* Fondo animado.

          Eran tres m.div de 800, 600 y 400 px con blur-3xl, animados en
          bucle infinito por framer-motion. Cada fotograma pasaba por JavaScript
          y repintaba superficies enormes desenfocadas, para siempre: es trabajo
          de hilo principal que no para nunca y compite con la carga inicial.

          Ahora son keyframes CSS que solo tocan `transform`, así que las compone
          la GPU sin JavaScript por medio, y se desactivan si el sistema pide
          reducir el movimiento. Visualmente es el mismo efecto. */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="hero-blob hero-blob-1 absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-primary-100/40 rounded-full blur-3xl opacity-60 mix-blend-multiply" />
        <div className="hero-blob hero-blob-2 absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-green-200/40 rounded-full blur-3xl opacity-60 mix-blend-multiply" />
        <div className="hero-blob hero-blob-3 absolute top-[30%] left-[30%] w-[400px] h-[400px] bg-emerald-100/30 rounded-full blur-3xl opacity-40 mix-blend-multiply" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <m.div
          className={cn(
            withWidget
              ? "grid lg:grid-cols-2 lg:grid-rows-[min-content_1fr] gap-5 lg:gap-x-14 lg:gap-y-6 items-start max-w-6xl mx-auto"
              : "max-w-5xl mx-auto text-center"
          )}
          variants={containerVariants}
          // Sin animación de entrada si el hero ya venía pintado en el HTML
          // prerenderizado (ver src/index.js, __PRERENDERED_BOOT).
          initial={typeof window !== "undefined" && window.__PRERENDERED_BOOT ? false : "hidden"}
          animate="visible"
        >
          {/* ---------------- Columna de texto ----------------

              Con el widget presente el orden del DOM es: texto → widget →
              botones. En móvil eso deja la zona de subida inmediatamente
              después del subtítulo, que es donde tiene que estar: con el orden
              anterior los dos botones, la nota y el contador la empujaban fuera
              de la pantalla justo en el dispositivo del que llega el tráfico de
              WhatsApp y del correo en frío.

              En escritorio la colocación explícita de `grid` vuelve a juntar
              texto y botones en la columna izquierda. */}
          <div className={cn(withWidget && "min-w-0 text-center lg:text-left lg:col-start-1 lg:row-start-1")}>
            <m.div
              variants={itemVariants}
              className={cn("flex mb-6", withWidget ? "hidden lg:flex lg:justify-start" : "justify-center mb-8")}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-primary-200/50 text-sm font-medium text-primary-800 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                {t("hero.badge")}
              </span>
            </m.div>

            <m.h1
              id="hero-title"
              variants={lcpSafeVariants}
              className={cn(
                "font-extrabold tracking-tight text-gray-900 mb-5 leading-[1.12]",
                withWidget
                  ? "text-[1.7rem] leading-[1.15] sm:text-4xl lg:text-5xl"
                  : "text-4xl sm:text-5xl md:text-7xl mb-6 md:mb-8 leading-[1.1]"
              )}
            >
              {customTitle || t("hero.title")}
            </m.h1>

            <m.p
              variants={itemVariants}
              className={cn(
                "text-gray-600 leading-relaxed",
                withWidget
                  ? "text-base sm:text-lg mb-5 sm:mb-8 max-w-xl mx-auto lg:mx-0"
                  : "text-xl md:text-2xl max-w-3xl mx-auto mb-12"
              )}
            >
              {customSubtitle || copy("hero.subtitle")}
            </m.p>

          </div>
          {/* ---------------- Columna del widget ---------------- */}
          {withWidget && (
            <m.div
              variants={itemVariants}
              id="try-free"
              // min-w-0: un hijo de grid no encoge por debajo de su contenido.
              // Sin esto, la tabla de líneas de un albarán ensanchaba todo el
              // hero más que la pantalla del móvil y la sección (overflow-hidden)
              // lo recortaba sin dejar hacer scroll.
              className="min-w-0 scroll-mt-24 lg:col-start-2 lg:row-start-1 lg:row-span-2"
            >
              <div className="rounded-3xl bg-white border border-gray-100 shadow-2xl shadow-primary-900/5 p-4 sm:p-7">
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  {copy("hero.widget_title")}
                </h2>
                <p className="text-sm text-gray-500 mb-3 sm:mb-5">{copy("hero.widget_note")}</p>
                <FreeExtractWidget variant={heroVariant()} />
              </div>

              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <FeaturePill icon={Layers} text={t("hero.feature_1")} compact />
                <FeaturePill icon={MessageCircle} text={t("hero.feature_2")} compact />
                <FeaturePill icon={Plug} text={t("hero.feature_3")} compact />
              </div>
            </m.div>
          )}
          {/* ---------------- Botones y prueba social ----------------
              Secundarios cuando el widget está presente: la acción principal es
              subir un documento, no registrarse. */}
          <div className={cn(withWidget && "min-w-0 lg:col-start-1 lg:row-start-2 lg:self-start")}>
            <m.div
              variants={itemVariants}
              className={cn(
                "flex flex-col",
                withWidget ? "items-center lg:items-start" : "items-center mb-12"
              )}
            >
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <m.a
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  href={signupHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={(e) => {
                    e.currentTarget.href = signupUrl(lang);
                    if (window.gtag) window.gtag("event", "signup_click", { method: "hero" });
                    if (window.fbq) window.fbq("track", "Lead", { content_name: "signup_hero" });
                  }}
                  className={cn(
                    "group relative inline-flex items-center justify-center rounded-full font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 overflow-hidden",
                    withWidget
                      ? "px-6 py-3 text-base text-primary-700 bg-white/80 border border-primary-200 hover:bg-white hover:border-primary-300"
                      : "px-8 py-4 text-lg text-white bg-primary-600 hover:bg-primary-700 shadow-lg shadow-primary-600/30"
                  )}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {t("hero.cta")}{" "}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  {!withWidget && (
                    <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-0" />
                  )}
                </m.a>

                <button
                  type="button"
                  onClick={scrollToHowItWorks}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-full font-semibold text-gray-700 bg-white/70 backdrop-blur-sm border border-gray-200 hover:border-primary-300 hover:text-primary-700 hover:bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400",
                    withWidget ? "px-5 py-3 text-base" : "px-6 py-4 text-lg"
                  )}
                >
                  <PlayCircle className="w-5 h-5" />
                  {t("hero.cta_secondary")}
                </button>
              </div>

              <p className="mt-3 text-sm font-medium text-gray-500">{t("hero.cta_note")}</p>

              {/* Prueba social: dato real, contado desde la base. */}
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-primary-600 tabular-nums">
                  +<CountUp to={15000} separator={thousandsSep} duration={2.2} />
                </span>
                <span className="text-sm text-gray-500">{t("hero.stat_label")}</span>
              </div>
            </m.div>

            {!withWidget && (
              <m.div
                variants={itemVariants}
                className="flex flex-wrap justify-center gap-4 md:gap-8 mt-10"
              >
                <FeaturePill icon={Layers} text={t("hero.feature_1")} />
                <FeaturePill icon={MessageCircle} text={t("hero.feature_2")} />
                <FeaturePill icon={Plug} text={t("hero.feature_3")} />
              </m.div>
            )}
          </div>

        </m.div>
      </div>
    </section>
  );
}

function FeaturePill({ icon: Icon, text, compact = false }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 shadow-sm transition-all duration-300",
        compact ? "px-3 py-2" : "px-5 py-3 hover:shadow-md hover:-translate-y-1"
      )}
    >
      <div className="p-1.5 rounded-full bg-primary-50 text-primary-600">
        <Icon className={compact ? "w-4 h-4" : "w-5 h-5"} />
      </div>
      <span className={cn("font-medium text-gray-700", compact ? "text-xs" : "text-base")}>
        {text}
      </span>
    </div>
  );
}
